import {
  Circle,
  GoogleMap,
  InfoWindow,
  Marker,
  OverlayView,
} from '@react-google-maps/api';
import { useEffect, useRef, useState, useCallback } from 'react';

import jobSiteIcon from '../../assets/img/icons/jobsite.svg';
import {
  MapRadiusInfoDiv,
  MapRadiusInfoText,
} from '../FieldServices/JobSites/jobSitesStyles';

const options = {
  streetViewControl: false,
  fullscreenControl: false,
  mapTypeControl: false,
  disableDoubleClickZoom: true,
  gestureHandling: 'greedy',
  mapId: process.env.REACT_APP_JOB_SITES_MAP_ID,
  draggable: true,
  zoomControl: true,
  scrollwheel: true,
  clickableIcons: false,
  keyboardShortcuts: true,
};

const containerStyle = {
  width: '100%',
  height: '85vh',
  borderLeft: '1px solid #e4e7eb',
  borderRadius: '0 10px 10px 0',
  // marginTop: "10px",
};
const center = {
  lat: 21.424090674479555,
  lng: 39.822141261515604,
};
const circleOptions = {
  strokeColor: '#2aa847',
  strokeWeight: 2,
  fillColor: '#2aa847',
  fillOpacity: 0.2,
  draggable: true,
  editable: true,
  clickable: true,
  zIndex: 1
};

const getPixelPositionOffset = (offsetWidth, offsetHeight, labelAnchor) => {
  return {
    x: labelAnchor.x,
    // x: offsetWidth + labelAnchor.x,
    y: offsetHeight + labelAnchor.y,
  };
};

const JobSitesMap = props => {
  const [showMarkerInfo, setShowMarkerInfo] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [circleCenter, setCircleCenter] = useState(null);
  const circleRef = useRef(null);
  const radiusChangeTimeoutRef = useRef(null);
  const [mapInstance, setMapInstance] = useState(null);

  const {
    currentPage,
    sitesList,
    activeSite,
    selectSite,
    singleAddressMarker,
    setAddressFromMap,
    setMap,
    radius,
    setRadius,
    isEditing = false,
  } = props;

  // Handle map load
  const handleMapLoad = useCallback((map) => {
    setMapInstance(map);
    setMap(map);
  }, [setMap]);

  // Focus map on the appropriate location
  useEffect(() => {
    if (mapInstance) {
      if (isEditing && singleAddressMarker) {
        // In edit mode, focus on the circle location with proper zoom
        const zoomLevel = Math.min(17, Math.max(15, Math.floor(20 - Math.log2(radius / 100))));
        mapInstance.panTo(singleAddressMarker);
        mapInstance.setZoom(zoomLevel);
      } else if (singleAddressMarker) {
        // In non-edit mode, just focus on the marker
        mapInstance.panTo(singleAddressMarker);
        mapInstance.setZoom(17);
      } else {
        // Default view
        mapInstance.panTo(center);
        mapInstance.setZoom(13);
      }
    }
  }, [mapInstance, singleAddressMarker, isEditing, radius]);

  const handleCircleDrag = (e) => {
    const newCenter = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng()
    };
    setCircleCenter(newCenter);
    setAddressFromMap({ latLng: e.latLng });
  };

  const handleRadiusChange = useCallback(() => {
    if (circleRef.current) {
      const newRadius = circleRef.current.getRadius();

      // Clear any existing timeout
      if (radiusChangeTimeoutRef.current) {
        clearTimeout(radiusChangeTimeoutRef.current);
      }

      // Set a new timeout to debounce the radius change
      radiusChangeTimeoutRef.current = setTimeout(() => {
        if (setRadius) {
          // Only update if the radius has actually changed by more than 5 meters
          if (Math.abs(Math.round(newRadius) - radius) > 5) {
            setRadius(Math.round(newRadius));
          }
        }
      }, 500); // 500ms debounce
    }
  }, [setRadius, radius]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (radiusChangeTimeoutRef.current) {
        clearTimeout(radiusChangeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <GoogleMap
      options={options}
      mapContainerStyle={containerStyle}
      center={center}
      zoom={13}
      onLoad={handleMapLoad}
      clickableIcons={false}
      onDblClick={location => setAddressFromMap(location)}
      onDragStart={() => setIsDragging(true)}
      onDragEnd={(e) => {
        setIsDragging(false);
        setAddressFromMap(e);
      }}
    >
      {currentPage === 'list' ? (
        <>
          {sitesList && sitesList.length
            ? sitesList.map(site => {
                return (
                  <>
                    <Marker
                      key={site.id}
                      position={{
                        lat: parseFloat(site.latitude),
                        lng: parseFloat(site.longitude),
                      }}
                      icon={{
                        url: jobSiteIcon,
                        scaledSize: {
                          height: 55,
                          width: 55,
                        },
                      }}
                      onClick={e => {
                        selectSite(site);
                      }}
                      draggable={true}
                      onDragEnd={e => setAddressFromMap(e)}
                    />
                    {activeSite &&
                    activeSite.id &&
                    activeSite.id === site.id ? (
                      <OverlayView
                        key={`ov-${site.id}`}
                        position={{
                          lat: parseFloat(site.latitude),
                          lng: parseFloat(site.longitude),
                        }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        getPixelPositionOffset={(x, y) =>
                          getPixelPositionOffset(x, y, { x: -30, y: -20 })
                        }
                      >
                        <MapRadiusInfoDiv>
                          <MapRadiusInfoText>
                            {activeSite.radius} m
                          </MapRadiusInfoText>
                        </MapRadiusInfoDiv>
                      </OverlayView>
                    ) : null}
                  </>
                );
              })
            : null}
          {activeSite && activeSite.id ? (
            <Circle
              center={{
                lat: parseFloat(activeSite.latitude),
                lng: parseFloat(activeSite.longitude),
              }}
              radius={activeSite.radius}
              options={{
                ...circleOptions,
                draggable: true,
                editable: true
              }}
              onLoad={circle => circleRef.current = circle}
              onDragEnd={handleCircleDrag}
              onRadiusChanged={handleRadiusChange}
            />
          ) : null}
        </>
      ) : currentPage === 'details' ? (
        singleAddressMarker ? (
          <>
            <Marker
              position={singleAddressMarker}
              icon={{
                url: jobSiteIcon,
                scale: 20,
              }}
              draggable
              onDragEnd={e => setAddressFromMap(e)}
            />
            <OverlayView
              key={`ovd`}
              position={singleAddressMarker}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              getPixelPositionOffset={(x, y) =>
                getPixelPositionOffset(x, y, { x: -30, y: -20 })
              }
            >
              <MapRadiusInfoDiv>
                <MapRadiusInfoText>{radius} m</MapRadiusInfoText>
              </MapRadiusInfoDiv>
            </OverlayView>
            <Circle
              center={singleAddressMarker}
              radius={radius}
              options={{
                ...circleOptions,
                draggable: true,
                editable: true
              }}
              onLoad={circle => circleRef.current = circle}
              onDragEnd={handleCircleDrag}
              onRadiusChanged={handleRadiusChange}
            />
          </>
        ) : null
      ) : null}
    </GoogleMap>
  );
};

export default JobSitesMap;
